import { createApi } from '@reduxjs/toolkit/query/react'
import axiosBaseQuery from 'api/axiosBaseQuery'

export interface RequestJournalEntry {
  id: string
  correlationId: string
  createdAt: string
  username: string | null
  userId: string | null
  roles: string | null
  method: string
  path: string
  queryString: string | null
  requestBody: string | null
  requestHeaders: string | null
  status: number
  responseBody: string | null
  responseHeaders: string | null
  durationMs: number
  bytesIn: number
  bytesOut: number
  clientIp: string | null
  userAgent: string | null
  referer: string | null
  errorMessage: string | null
}

export interface RequestJournalPage {
  content: RequestJournalEntry[]
  totalElements: number
  totalPages: number
  number: number
  size: number
}

export interface RequestJournalQuery {
  username?: string
  path?: string
  clientIp?: string
  method?: string
  status?: number
  from?: string
  to?: string
  page?: number
  size?: number
}

export interface JournalFilterOptions {
  clientIps: string[]
}

export interface EntityRevision {
  revision: number
  metadata: {
    id: number
    revisionDate?: string
    timestamp?: number
    username: string | null
    userId: string | null
    clientIp: string | null
    requestUri: string | null
    userAgent: string | null
    correlationId: string | null
  }
  snapshot: Record<string, unknown>
  revType?: 'ADD' | 'MOD' | 'DEL' | string
}

export interface EntityHistoryQuery {
  entityName: string
  id: string
}

export interface TimelineHistoryQuery {
  entityType: string
  entityId: string
}

export interface TimelineFieldChange {
  field: string
  oldValue: unknown
  newValue: unknown
  updatedAt?: string | null
  updatedBy?: string | null
}

export interface TimelineEntry {
  id?: number | string
  revision?: number
  action?: string
  entityType?: string
  entityId?: string
  changedAt?: string
  revisionDate?: string
  createdAt?: string
  updatedAt?: string
  timestamp?: string
  changedBy?: string | null
  changedById?: string | null
  username?: string | null
  userId?: string | null
  clientIp?: string | null
  requestUri?: string | null
  correlationId?: string | null
  description?: string | null
  changes?: TimelineFieldChange[] | Record<string, unknown> | null
  snapshot?: Record<string, unknown> | null
  metadata?: {
    revisionDate?: string
    username?: string | null
    userId?: string | null
  } | null
  [key: string]: unknown
}

export interface EntityListQuery {
  entityName: string
  page?: number
  size?: number
}

export interface EntityListPage {
  content: Array<Record<string, unknown>>
  totalElements: number
  totalPages: number
  number: number
  size: number
}

export const auditApi = createApi({
  reducerPath: 'auditApi',
  baseQuery: axiosBaseQuery({ baseUrl: process.env.REACT_APP_BASE_URL ?? '' }),
  tagTypes: ['Journal', 'History'],
  endpoints: (builder) => ({
    getRequestJournal: builder.query<RequestJournalPage, RequestJournalQuery>({
      query: (params) => ({
        url: '/admin/request-journal',
        method: 'GET',
        params
      }),
      providesTags: ['Journal']
    }),
    getJournalByCorrelationId: builder.query<RequestJournalEntry[], string>({
      query: (correlationId) => ({
        url: `/admin/request-journal/${correlationId}`,
        method: 'GET'
      }),
      providesTags: ['Journal']
    }),
    getJournalFilterOptions: builder.query<JournalFilterOptions, number | void>(
      {
        query: (days) => ({
          url: '/admin/request-journal/filters',
          method: 'GET',
          params: days ? { days } : undefined
        }),
        providesTags: ['Journal']
      }
    ),
    getEntityHistory: builder.query<EntityRevision[], EntityHistoryQuery>({
      query: ({ entityName, id }) => ({
        url: `/admin/history/${entityName}/${id}`,
        method: 'GET'
      }),
      providesTags: ['History']
    }),
    getEntityList: builder.query<EntityListPage, EntityListQuery>({
      query: ({ entityName, page = 0, size = 20 }) => ({
        url: `/admin/history/${entityName}`,
        method: 'GET',
        params: { page, size }
      }),
      providesTags: ['History']
    }),
    getTimelineHistory: builder.query<TimelineEntry[], TimelineHistoryQuery>({
      query: ({ entityType, entityId }) => ({
        url: '/admin/history',
        method: 'GET',
        params: { entityType, entityId }
      }),
      transformResponse: (response: unknown): TimelineEntry[] => {
        if (Array.isArray(response)) return response as TimelineEntry[]
        const content = (response as { content?: unknown })?.content
        if (Array.isArray(content)) return content as TimelineEntry[]
        return []
      },
      providesTags: ['History']
    })
  })
})

export const {
  useGetRequestJournalQuery,
  useLazyGetRequestJournalQuery,
  useGetJournalFilterOptionsQuery,
  useGetJournalByCorrelationIdQuery,
  useLazyGetJournalByCorrelationIdQuery,
  useGetEntityHistoryQuery,
  useLazyGetEntityHistoryQuery,
  useGetEntityListQuery,
  useGetTimelineHistoryQuery
} = auditApi
